import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    BatterySetDto,
    BatterySetService,
    FEATURE_ORGANISATION_RELATIONSHIPS,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    OrganisationDto,
    OrganisationService,
    PersonsOrganisationDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BatterySetDetails } from '../battery-set-details.component';

@Component({
    selector: 'battery-set-view',
    templateUrl: './battery-set-view.component.html'
})
export class BatterySetView {
    @Input() currentOrganisation: PersonsOrganisationDto;
    @Input() batterySet: BatterySetDto;
    @Input() modal: BsModalRef<BatterySetDetails>;
    @Output() edit = new EventEmitter<void>();
    @Output() deleted = new EventEmitter<void>();

    hasEdit = false;
    hasDelete = false;
    canEdit = true;
    canDelete?: boolean;
    isOwner?: boolean;
    hasCustodian = false;

    owner?: OrganisationDto;

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private batterySetService: BatterySetService,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService,
        private organisationService: OrganisationService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('batterySet' in changes || 'currentOrganisation' in changes) {
            this.refreshPermissions();
            this.refreshOwner();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshPermissions() {
        this.hasEdit = this.batterySet.availableActions.hasEdit;
        this.hasDelete = this.batterySet.availableActions.hasDelete;
        this.canEdit = this.batterySet.availableActions.canEdit;
        this.canDelete = this.batterySet.availableActions.canDelete;
        this.isOwner =
            this.batterySet.organisationId === this.currentOrganisation.id;
        this.hasCustodian = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_ORGANISATION_RELATIONSHIPS
        );
    }


    private refreshOwner() {
        if (this.batterySet == null) {
            return;
        }

        this.organisationService
            .findById(this.batterySet.organisationId, this.currentOrganisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(owner => (this.owner = owner));
    }

    onEdit() {
        this.edit.emit();
    }

    delete() {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete Battery',
                'Are you sure you wish to delete this battery set?',
                'Delete',
                () =>
                    this.batterySetService
                        .delete(this.batterySet.id)
                        .toPromise()
            )
            .then(battery => {
                this.logging.success(`Battery set has been deleted`);
                this.deleted.next();
                doneWorking();
            })
            .catch(() => doneWorking());
    }
}
